import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IDSButton, IDSInput, IDSAccordion, IDSCheckButton, IDSSelectMultiple, IDSCheckbox } from '@inera/ids-react';
import ConfirmDialog from '../ConfirmDialog';
import StatusAlert from '../StatusAlert';
import { fetchAllUsers, fetchRoles, assignRole, unassignRole } from '../../services/usersAdminAPI';
import { updateUser, deleteUser } from '../../services/organisationsAdminAPI';
import { formatRoleName } from '../../utils/tokenUtils';
import { validateUser } from '../../utils/adminValidation';
import {
  fetchDepartments,
  fetchDepartmentsForUser,
  addDepartmentMember,
  removeDepartmentMember,
} from '../../services/departmentsAdminAPI';
import { belongsToReviewingOrg } from '../../utils/reviewingOrganisation';

const normalize = (s) => String(s).toLowerCase().replace(/[_\s]/g, '');

// Roles relevant for a user = roles whose org prefix matches one of the user's organisations
const rolesForUser = (user, allRoles) => {
  const orgPrefixes = (user.organisations || []).map(normalize);
  if (orgPrefixes.length === 0) return [];
  return allRoles.filter(role => {
    if (!role.name) return false;
    const match = normalize(role.name).match(/^([^-:]+)/);
    return match && orgPrefixes.includes(match[1]);
  });
};

// ── User form (edit) ──────────────────────────────────────────────────────────
const UserForm = ({ initial = {}, onSave, onCancel }) => {
  const [firstName, setFirstName] = useState(initial.firstName || '');
  const [lastName, setLastName] = useState(initial.lastName || '');
  const [email, setEmail] = useState(initial.email || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateUser({ email });
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setLoading(true);
    setError(null);
    try {
      await onSave({ firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim() });
    } catch (err) {
      setError(err.message || "Något gick fel. Försök igen.");
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate style={{ background: 'var(--ids-palette-neutral-95)', border: '1px solid var(--ids-palette-neutral-90)', borderRadius: '6px', padding: '1.25rem', marginBottom: '1rem' }}>
      <div className="ids-mb-3">
        <IDSInput label="Förnamn" type="text" value={firstName} onChange={e => setFirstName(e.target.value)} />
      </div>
      <div className="ids-mb-3">
        <IDSInput label="Efternamn" type="text" value={lastName} onChange={e => setLastName(e.target.value)} />
      </div>
      <div className="ids-mb-3">
        <IDSInput
          label="E-post *"
          type="email"
          value={email}
          invalid={!!fieldErrors.email}
          errorMsg={fieldErrors.email}
          onChange={e => { setEmail(e.target.value); setFieldErrors(prev => ({ ...prev, email: null })); }}
        />
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <IDSButton size="s" type="submit" disabled={loading}>{loading ? 'Sparar...' : 'Uppdatera'}</IDSButton>
        <IDSButton size="s" type="button" onClick={onCancel}>Avbryt</IDSButton>
      </div>
      <StatusAlert error={error} className="ids-mt-3" />
    </form>
  );
};

// ── Role assignment pills ─────────────────────────────────────────────────────
const RoleAssignments = ({ user, allRoles, onUserUpdate }) => {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const assignedIds = new Set((user.roles || []).map(r => r.id));
  const availableRoles = rolesForUser(user, allRoles);

  const toggle = async (role) => {
    setBusy(role.id);
    setError(null);
    try {
      if (assignedIds.has(role.id)) {
        await unassignRole(user.keycloakUserId, role.id);
        onUserUpdate(user.keycloakUserId, { roles: user.roles.filter(r => r.id !== role.id) });
      } else {
        await assignRole(user.keycloakUserId, role.id);
        onUserUpdate(user.keycloakUserId, {
          roles: [...(user.roles || []), { id: role.id, name: role.name }],
        });
      }
    } catch (err) {
      setError(err.message || "Något gick fel. Försök igen.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ marginTop: '1.25rem', paddingTop: '0.75rem', borderTop: '1px solid var(--ids-palette-neutral-90)' }}>
      <p style={{ fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.85rem', color: 'var(--ids-palette-neutral-30)' }}>Roller:</p>
      {availableRoles.length === 0 ? (
        <p style={{ color: 'var(--ids-palette-neutral-70)', fontSize: '0.9rem' }}>Inga roller tillgängliga för användarens organisationer.</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
          {availableRoles.map(role => {
            const assigned = assignedIds.has(role.id);
            return (
              <IDSCheckButton
                key={`${role.id}-${assigned}`}
                defaultChecked={assigned}
                disabled={busy === role.id}
                onChange={() => toggle(role)}
              >
                {formatRoleName(role.name)}
              </IDSCheckButton>
            );
          })}
        </div>
      )}
      <StatusAlert error={error} className="ids-mt-3" />
    </div>
  );
};

// ── Department assignments (E2E) ──────────────────────────────────────────────
// Departments decide which E2E submissions a reviewer sees, so they are only
// relevant for users in the reviewing organisation.
const DepartmentAssignments = ({ user, allDepartments }) => {
  const [assignedIds, setAssignedIds] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    Promise.resolve(fetchDepartmentsForUser(user.keycloakUserId))
      .then((departments) => {
        if (!cancelled) setAssignedIds(new Set((departments || []).map(d => d.departmentId)));
      })
      .catch(err => {
        if (!cancelled) setError(err.message || 'Kunde inte hämta avdelningar.');
      });

    return () => { cancelled = true; };
  }, [user.keycloakUserId]);

  const toggle = async (department) => {
    if (!assignedIds) return;

    const departmentId = department.departmentId;
    const assigned = assignedIds.has(departmentId);

    setBusy(departmentId);
    setError(null);
    try {
      if (assigned) {
        await removeDepartmentMember(departmentId, user.keycloakUserId);
      } else {
        await addDepartmentMember(departmentId, user.keycloakUserId, {
          name: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username,
          email: user.email,
        });
      }

      setAssignedIds(prev => {
        const next = new Set(prev);
        if (assigned) next.delete(departmentId);
        else next.add(departmentId);
        return next;
      });
    } catch (err) {
      setError(err.message || 'Något gick fel. Försök igen.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ marginTop: '1.25rem', paddingTop: '0.75rem', borderTop: '1px solid var(--ids-palette-neutral-90)' }}>
      <p style={{ fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.85rem', color: 'var(--ids-palette-neutral-30)' }}>Avdelningar (E2E):</p>
      {allDepartments.length === 0 ? (
        <p style={{ color: 'var(--ids-palette-neutral-70)', fontSize: '0.9rem' }}>Inga avdelningar är upplagda.</p>
      ) : assignedIds === null && !error ? (
        <p style={{ fontSize: '0.9rem' }}>Laddar avdelningar...</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
          {allDepartments.map(department => {
            const assigned = assignedIds?.has(department.departmentId) || false;
            return (
              <IDSCheckButton
                key={`${department.departmentId}-${assigned}`}
                defaultChecked={assigned}
                disabled={busy === department.departmentId}
                onChange={() => toggle(department)}
              >
                {department.name}
              </IDSCheckButton>
            );
          })}
        </div>
      )}
      <StatusAlert error={error} className="ids-mt-3" />
    </div>
  );
};

// ── User accordion ────────────────────────────────────────────────────────────
const UserAccordion = ({ user, allRoles, allDepartments, onUserUpdate, onUserDelete }) => {
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async (payload) => {
    await updateUser(user.keycloakUserId, payload);
    onUserUpdate(user.keycloakUserId, {
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
    });
    setEditing(false);
  };

  const doDelete = async () => {
    setConfirmDelete(false);
    setDeleting(true);
    setError(null);
    try {
      await deleteUser(user.keycloakUserId);
      onUserDelete(user.keycloakUserId);
    } catch (err) {
      setError(err.message || "Något gick fel. Försök igen.");
      setDeleting(false);
    }
  };

  const orgLabel = (user.organisations || []).join(', ');

  return (
    <>
      {confirmDelete && (
        <ConfirmDialog
          message={`Vill du ta bort användaren "${user.username}"?`}
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
      <IDSAccordion
        headline={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h3>{user.username}</h3>
            <span style={{ fontSize: '0.85rem', color: 'var(--ids-palette-neutral-60)' }}>
              {user.email}{orgLabel ? ` · ${orgLabel}` : ''}
            </span>
          </div>
        }
        headlineSize="m"
        level={1}
      >
        <div>
          {(user.firstName || user.lastName) && (
            <p className="ids-mb-4" style={{ color: 'var(--ids-palette-neutral-50)', fontSize: '0.9rem' }}>
              {[user.firstName, user.lastName].filter(Boolean).join(' ')}
            </p>
          )}

          {/* User actions */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <IDSButton size="s" type="button" onClick={() => setEditing(e => !e)}>
              {editing ? 'Avbryt redigering' : 'Redigera användare'}
            </IDSButton>
            <IDSButton size="s" type="button" disabled={deleting} onClick={() => setConfirmDelete(true)}>
              {deleting ? '...' : 'Ta bort användare'}
            </IDSButton>
          </div>

          <StatusAlert error={error} className="ids-mb-3" />

          {editing && (
            <UserForm
              initial={{ firstName: user.firstName, lastName: user.lastName, email: user.email }}
              onSave={handleSave}
              onCancel={() => setEditing(false)}
            />
          )}

          <RoleAssignments user={user} allRoles={allRoles} onUserUpdate={onUserUpdate} />

          {belongsToReviewingOrg(user) && (
            <DepartmentAssignments user={user} allDepartments={allDepartments} />
          )}
        </div>
      </IDSAccordion>
    </>
  );
};

// ── Main layout ───────────────────────────────────────────────────────────────
const UsersAdminLayout = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrgs, setSelectedOrgs] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [usersData, rolesData, departmentsData] = await Promise.all([
          fetchAllUsers(),
          fetchRoles(),
          // Departments live in the E2E service and are optional here
          Promise.resolve(fetchDepartments()).catch(() => []),
        ]);
        setUsers(usersData);
        setRoles(rolesData);
        setDepartments((departmentsData || []).filter(d => d.active));
      } catch (err) {
        setError(err.message || "Något gick fel. Försök igen.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleUserUpdate = (keycloakUserId, patch) => {
    setUsers(prev => prev.map(u => u.keycloakUserId === keycloakUserId ? { ...u, ...patch } : u));
  };

  const handleUserDelete = (keycloakUserId) => {
    setUsers(prev => prev.filter(u => u.keycloakUserId !== keycloakUserId));
  };

  const orgNames = [...new Set(users.flatMap(u => u.organisations || []))]
    .sort((a, b) => a.localeCompare(b, 'sv'));

  const toggleOrgFilter = (name) => {
    setSelectedOrgs(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  const visibleUsers = selectedOrgs.length === 0
    ? users
    : users.filter(u => (u.organisations || []).some(n => selectedOrgs.includes(n)));

  return (
    <main className="ids-content ids-p-6" style={{ maxWidth: '860px', margin: '2rem auto' }}>
      <h1 className="ids-heading-l ids-mb-2">Användare</h1>

      <section className="ids-mt-6">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 className="ids-heading-m">Alla användare</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <IDSButton size="s" secondary type="button" onClick={() => navigate('/departments')}>Avdelningar</IDSButton>
            <IDSButton size="s" type="button" onClick={() => navigate('/createuser')}>+ Skapa ny användare</IDSButton>
          </div>
        </div>

        {!loading && !error && orgNames.length > 0 && (
          <div className="ids-mb-4" style={{ maxWidth: '320px' }}>
            <IDSSelectMultiple
              label="Filtrera på organisation"
              placeholder="Alla organisationer"
              selectedLabel="vald"
              selectedLabelPlural="valda"
              numbCheckedBoxes={selectedOrgs.length}
              maxHeight="12rem"
            >
              {orgNames.map(name => (
                <IDSCheckbox
                  key={name}
                  checked={selectedOrgs.includes(name)}
                  onChange={() => toggleOrgFilter(name)}
                >
                  {name}
                </IDSCheckbox>
              ))}
            </IDSSelectMultiple>
          </div>
        )}

        {loading && <p>Laddar...</p>}
        <StatusAlert error={error} />
        {!loading && !error && users.length === 0 && (
          <p style={{ color: 'var(--ids-palette-neutral-60)' }}>Inga användare hittades.</p>
        )}
        {!loading && !error && users.length > 0 && visibleUsers.length === 0 && (
          <p style={{ color: 'var(--ids-palette-neutral-60)' }}>Inga användare matchar filtret.</p>
        )}

        {visibleUsers.map(user => (
          <UserAccordion
            key={user.keycloakUserId}
            user={user}
            allRoles={roles}
            allDepartments={departments}
            onUserUpdate={handleUserUpdate}
            onUserDelete={handleUserDelete}
          />
        ))}
      </section>
    </main>
  );
};

export default UsersAdminLayout;
